import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { SubmissionErrorBoundary } from '../../../components/errors/SubmissionErrorBoundary';
import { TsForm } from '../../../components/form/Form';
import { MapField } from '../../../components/form/MapField';
import { SubmitCancelButtons } from '../../../components/form/SubmitCancelButtons';
import { TextFieldWithOptionalLabel } from '../../../components/form/TextInput';
import { MapPoint } from '../../../components/shared/maps/Map';
import { Modal } from '../../../components/shared/modal/Modal';
import { handleMutationResult } from '../../../helpers/toastHelper';
import { useCreateLocationCoordinatesMutation } from '../../../services/gql/graphql.generated';
import { WorkLocation } from '../../../services/location/location-helper';
import { useLocationService } from '../../../services/location/LocationService';
import shiftStrings from '../../shifts/shifts.translations';

interface AddGeolocationModalProps {
  open: boolean;
  onClose: () => void;
  workLocation: WorkLocation;
  mapPoints: MapPoint[];
}

interface AddGeolocationFormFields {
  locationName: string;
  geolocation: {
    longitude: number;
    latitude: number;
  };
}

export const AddGeolocationModal: FC<AddGeolocationModalProps> = ({ open, onClose, workLocation, mapPoints }) => {
  const locationId = useLocationService(state => state.selectedLocation?.id);
  const methods = useForm<AddGeolocationFormFields>();
  const [{ fetching }, CreateLocationCoordinates] = useCreateLocationCoordinatesMutation();

  const addLocation = async (values: AddGeolocationFormFields) => {
    if (!locationId) return;
    const { geolocation, locationName } = values;
    const { longitude, latitude } = geolocation;
    const result = await CreateLocationCoordinates({
      locationCoordinates: {
        locationName,
        locationId,
        longitude,
        latitude
      }
    });

    await handleMutationResult(result, {
      successMessage: "Geolocation successfully added.",
      errorMessage: "Unable to add geolocation.",
      onSuccess: onClose
    });
  };

  return (
    <Modal open={open} onClose={onClose} header={shiftStrings.addGeolocation.modalTitle} className="flex flex-col">
      <p className="mb-2">{shiftStrings.addGeolocation.modalDescription}</p>
      <SubmissionErrorBoundary>
        {/* @ts-ignore Type instantiation is excessively deep and possibly infinite */}
        <TsForm methods={methods} onSubmit={addLocation}>
          <div className="flex flex-col py-2">
            <div className="mb-14">
              <MapField
                id="geolocation"
                name="geolocation"
                required
                validationMessage="Please select a location"
                title={shiftStrings.addGeolocation.geolocationLabel}
                origin={workLocation}
                className="h-48 sm:h-80 rounded"
                center={[workLocation.position.longitude, workLocation.position.latitude - 0.001]}
                points={mapPoints}
                value={methods.getValues('geolocation') || null}
                onChange={point => methods.setValue('geolocation', point)}
              />
            </div>
            <TextFieldWithOptionalLabel
              id="locationName"
              name={'locationName'}
              title={shiftStrings.addGeolocation.siteNameLabel}
              validationMessage={shiftStrings.addGeolocation.geolocationRequired}
              required
              value={methods.getValues('locationName') || ''}
              onChange={value => methods.setValue('locationName', value)}
            />
          </div>
          <SubmitCancelButtons onComplete={onClose} loading={fetching} />
        </TsForm>
      </SubmissionErrorBoundary>
    </Modal>
  );
};
